import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, roleConfig } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus, Loader2, ArrowLeft, Trash2, Pencil, LogIn, Mail, Building2,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_GROUPS = [
  { value: 'jefatura', label: 'Jefaturas', desc: 'Asigna y supervisa tickets/proyectos' },
  { value: 'solucionador', label: 'Solucionadores', desc: 'Resuelven tickets asignados' },
  { value: 'solicitante', label: 'Solicitantes', desc: 'Crean tickets/solicitudes' },
];

const emptyForm = { email: '', password: '', first_name: '', last_name: '', role: 'solicitante' };

export default function CompanyDetailPage() {
  const { id: companyId } = useParams();
  const navigate = useNavigate();
  const { switchCompany, activeCompanyId } = useAuth();

  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      const [cRes, uRes] = await Promise.all([
        axios.get(`${API_URL}/companies/${companyId}`),
        axios.get(`${API_URL}/users`, { params: { company_id: companyId } }),
      ]);
      setCompany(cRes.data);
      setUsers(uRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar la empresa');
      navigate('/empresas');
    } finally {
      setLoading(false);
    }
  }, [companyId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = (role) => {
    setForm({ ...emptyForm, role });
    setEditTarget(null);
    setCreateOpen(true);
  };

  const openEdit = (user) => {
    setForm({
      email: user.email,
      password: '',
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
    });
    setEditTarget(user);
    setCreateOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      if (editTarget) {
        const payload = {
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
        };
        await axios.patch(`${API_URL}/users/${editTarget.id}`, payload);
        toast.success('Usuario actualizado');
      } else {
        const payload = {
          email: form.email,
          password: form.password,
          first_name: form.first_name,
          last_name: form.last_name,
          role: form.role,
          company_id: companyId,
        };
        await axios.post(`${API_URL}/users`, payload);
        toast.success('Usuario creado');
      }
      setCreateOpen(false);
      setForm(emptyForm);
      setEditTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar usuario');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await axios.delete(`${API_URL}/users/${deleteTarget.id}`);
      toast.success('Usuario eliminado');
      setDeleteTarget(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSwitch = async () => {
    try {
      await switchCompany(companyId);
      toast.success(`Trabajando como ${company.name}`);
    } catch (e) {
      toast.error('No se pudo cambiar de contexto');
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!company) return null;

  return (
    <div className="space-y-6" data-testid="company-detail-page">
      {/* Back + header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/empresas')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7FD02E]/20 to-[#7FD02E]/5 flex items-center justify-center text-[#7FD02E] text-xl font-bold">
              {company.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-gray-500 font-mono">{company.slug}</span>
                <Badge variant={company.is_active ? 'default' : 'secondary'}>
                  {company.is_active ? 'Activa' : 'Inactiva'}
                </Badge>
                <Badge variant="outline" className="gap-1">
                  <Building2 className="w-3 h-3" /> {users.length} usuarios
                </Badge>
              </div>
            </div>
          </div>
        </div>
        {activeCompanyId !== companyId && (
          <Button onClick={handleSwitch} variant="outline">
            <LogIn className="w-4 h-4 mr-2" /> Trabajar como esta empresa
          </Button>
        )}
      </div>

      {/* Users by role */}
      <div className="space-y-6">
        {ROLE_GROUPS.map((group) => {
          const roleUsers = users.filter((u) => u.role === group.value);
          return (
            <Card key={group.value}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Badge className={roleConfig[group.value]?.color}>
                      {roleConfig[group.value]?.label || group.label}
                    </Badge>
                    <div>
                      <h3 className="font-semibold text-gray-900">{group.label}</h3>
                      <p className="text-xs text-gray-500">{group.desc}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => openCreate(group.value)}
                    className="bg-[#7FD02E] hover:bg-[#6BB825] text-white"
                    data-testid={`add-${group.value}-btn`}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Agregar
                  </Button>
                </div>
                {roleUsers.length === 0 ? (
                  <div className="text-sm text-gray-400 italic py-4 text-center border border-dashed border-gray-200 rounded-lg">
                    Sin usuarios en este rol.
                  </div>
                ) : (
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {roleUsers.map((u) => (
                      <div
                        key={u.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">
                            {u.first_name} {u.last_name}
                          </p>
                          <p className="text-xs text-gray-500 flex items-center gap-1 truncate">
                            <Mail className="w-3 h-3" /> {u.email}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="w-7 h-7"
                            onClick={() => openEdit(u)}
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="w-7 h-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteTarget(u)}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) { setEditTarget(null); setForm(emptyForm); } }}>
        <DialogContent>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>
                {editTarget ? 'Editar usuario' : 'Nuevo usuario'}
              </DialogTitle>
              <DialogDescription>
                {editTarget
                  ? `Modifica datos de ${editTarget.first_name} ${editTarget.last_name}.`
                  : `Crea un usuario para la empresa ${company.name}.`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="first_name">Nombre *</Label>
                  <Input
                    id="first_name"
                    required
                    value={form.first_name}
                    onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="last_name">Apellido *</Label>
                  <Input
                    id="last_name"
                    required
                    value={form.last_name}
                    onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  disabled={!!editTarget}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                {editTarget && (
                  <p className="text-xs text-gray-400 mt-1">El email no se puede modificar.</p>
                )}
              </div>
              {!editTarget && (
                <div>
                  <Label htmlFor="password">Contraseña *</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={6}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="role">Rol *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger id="role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_GROUPS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {roleConfig[r.value]?.label || r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={actionLoading}
                className="bg-[#7FD02E] hover:bg-[#6BB825] text-white"
              >
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editTarget ? 'Guardar cambios' : 'Crear usuario'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete user confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{deleteTarget?.first_name} {deleteTarget?.last_name}</strong> ({deleteTarget?.email}).
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={actionLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
